import React, { FC, useState, useMemo, useEffect } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { addStickyProps } from 'utils/analytics'
import OverlayMount from 'layouts/components/OverlayMount'
import { usePageLoad } from 'store/hooks'
import PageTitle from 'components/PageTitle'
import RealmBusiness from 'svgs/realm-business'
import { Block } from './Availabilities'
import { Calendar, VendorSiteVisitSchedulerInnerStatus } from './types'
import Unscheduled from './Unscheduled'
import Scheduled from './Scheduled'
import Canceled from './Canceled'
import Declined from './Declined'
import Screening from './Screening'
import Reassigned from './Reassigned'
import SiteVisitCanceled from './SiteVisitCanceled'
import SiteVisitExpired from './SiteVisitExpired'
import SiteVisitDeclined from './SiteVisitDeclined'
import SiteVisitDisqualified from './SiteVisitDisqualified'
import Blocked from './Blocked'
import styles from './styles.module.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

interface VendorSiteVisitProps {
  calendar: Calendar
}

const VendorSiteVisit: FC<VendorSiteVisitProps> = ({ calendar: initialCalendar }) => {
  const [calendar, setCalendar] = useState(initialCalendar)

  const projectTypesWithInsufficientPhotos = useMemo(() => {
    if (!calendar.current_vendor_contact.vendor_insufficient_photos_block) return []

    return calendar.property.project_types.filter((projectType) =>
      calendar.current_vendor_contact.vendor_project_types_with_insufficient_photos.includes(projectType)
    )
  }, [calendar])

  useEffect(() => {
    if (!calendar.impersonated) return

    addStickyProps({ impersonated: calendar.impersonated }, true)
  }, [calendar.impersonated])

  const availabilities = useMemo(() => {
    // eventDurationSize
    const eventDurationSize = calendar.event_duration * 60 * 1000
    // Increment size will be 30m, or if our event duration is smaller, that.
    const incrementSize = Math.max(30 * 60 * 1000, calendar.event_duration)
    // Split into even length time blocks.
    // Only allow availabilities to start and end at the top of the increment size.
    return calendar.availabilities.reduce((newAvail, block) => {
      const origStart = dayjs(block.time.start).tz(calendar.property.time_zone).toDate()
      const origEnd = dayjs(block.time.end).tz(calendar.property.time_zone).toDate()

      const start = Math.ceil(origStart.getTime() / incrementSize) * incrementSize
      const lastStart = origEnd.getTime() - eventDurationSize
      for (let x = start; x <= lastStart; x += incrementSize) {
        newAvail.push({
          start: dayjs(x).tz(calendar.property.time_zone).toDate(),
          end: dayjs(x + eventDurationSize)
            .tz(calendar.property.time_zone)
            .toDate(),
        })
      }
      return newAvail
    }, [] as Block[])
  }, [calendar])

  const status: 'unscheduled' | 'scheduled' | 'canceled' | 'declined' = useMemo(() => {
    if (calendar.event) {
      if (calendar.event.status == 'canceled') return 'canceled'
      if (calendar.event.status == 'declined') return 'declined'

      if (['scheduled', 'occurring', 'completed'].includes(calendar.event.status)) return 'scheduled'
    }

    return 'unscheduled' // Our default.
  }, [calendar])

  const [innerStatus, setInnerStatus] = useState<VendorSiteVisitSchedulerInnerStatus>(
    projectTypesWithInsufficientPhotos.length > 0
      ? 'blocked'
      : availabilities.length == 0
        ? null
        : status == 'unscheduled'
          ? calendar.property.site_visit_status == 'Canceled'
            ? 'canceled'
            : calendar.property.site_visit_status == 'Request Expired'
              ? 'expired'
              : calendar.property.site_visit_status == 'Request Declined'
                ? 'declined'
                : calendar.property.site_visit_status == 'Disqualified'
                  ? 'disqualified'
                  : calendar.property.site_visit_status == 'Reschedule requested by homeowner'
                    ? null
                    : 'screening'
          : null
  )

  // Scroll to the top  of the page any time our status changes.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' } as any)
    } catch {
      // Fallback in case the above isn't supported.
      window.scrollTo(0, 0)
    }
  }, [status])

  // Any time our calendar changes, update CSRF
  useEffect(() => {
    if (!window) return
    document.querySelector('meta[name=csrf-param]')?.setAttribute('content', calendar.csrf_param)
    document.querySelector('meta[name=csrf-token]')?.setAttribute('content', calendar.csrf_token)
  }, [calendar])

  usePageLoad('vendor-site-visit-scheduler')

  return (
    <OverlayMount>
      <PageTitle title="Schedule Site Visit" />
      {calendar.impersonated && (
        <div className={styles.impersonation}>
          {'You are now impersonating '}
          {<strong>{calendar.current_vendor_contact.email}</strong>}
        </div>
      )}
      {innerStatus == 'screening' ? (
        <Screening calendar={calendar} setCalendar={setCalendar} setInnerStatus={setInnerStatus} />
      ) : innerStatus == 'reassigned' ? (
        <Reassigned vendorContact={calendar.assigned_vendor_contact} />
      ) : innerStatus == 'canceled' ? (
        <SiteVisitCanceled />
      ) : innerStatus == 'expired' ? (
        <SiteVisitExpired />
      ) : innerStatus == 'declined' ? (
        <SiteVisitDeclined />
      ) : innerStatus == 'disqualified' ? (
        <SiteVisitDisqualified />
      ) : innerStatus == 'blocked' ? (
        <Blocked projectTypes={projectTypesWithInsufficientPhotos} />
      ) : (
        <div className="tw:relative tw:bg-neutrals-100 tw:min-h-screen">
          <div
            className={cx('tw:absolute tw:inset-0 tw:h-[392px] tw:sm:h-[632px]', {
              [styles.scheduled]: status == 'scheduled',
            })}
          />
          <div className="tw:relative tw:pt-4 tw:md:w-lg tw:md:mx-auto tw:md:pb-16">
            <RealmBusiness className="tw:h-10 tw:mx-auto tw:px-6" />
            <div>
              {status == 'declined' ? (
                <Declined calendar={calendar} />
              ) : status == 'canceled' ? (
                <Canceled calendar={calendar} onCalendarChange={setCalendar} />
              ) : status == 'scheduled' ? (
                <Scheduled calendar={calendar} availabilities={availabilities} onCalendarChange={setCalendar} />
              ) : (
                <Unscheduled
                  calendar={calendar}
                  availabilities={availabilities}
                  onCalendarChange={setCalendar}
                  setInnerStatus={setInnerStatus}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </OverlayMount>
  )
}

export default VendorSiteVisit
